
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import "./NewFeatureFilmAI.css";

const API_ROOT = "https://backend.whalesai.com/api/v1";

const REQUIRED_FIELDS = [
  "protagonistAndDesire",
  "centralConflictAndStakes",
  "storyWorldAndSetting",
  "characterTransformation",
  "themeAndMoral",
  "targetMarketAndCulturalFocus",
  "releaseType",
  "primaryGenre",
  "toneAndEmotionalExperience",
  "targetAudience",
  "featureFilmRuntime",
  "storyScaleAndScope",
];

const FIELD_CONFIG = [
  {
    name: "protagonistAndDesire",
    label: "Protagonist & Desire",
    hint: "Who is the main character, and what do they want?",
    placeholder:
      "Describe your protagonist, their goal, motivation, and what they are willing to risk.",
    group: "Story Pillars",
  },
  {
    name: "centralConflictAndStakes",
    label: "Central Conflict & Stakes",
    hint: "What stands in the way, and what happens if they fail?",
    placeholder:
      "Describe the central conflict, antagonist or opposing force, and the consequences.",
    group: "Story Pillars",
  },
  {
    name: "storyWorldAndSetting",
    label: "Story World & Setting",
    hint: "Where and when does the story unfold?",
    placeholder:
      "Describe the time period, locations, world rules, and atmosphere.",
    group: "Story Pillars",
  },
  {
    name: "characterTransformation",
    label: "Character Transformation",
    hint: "How does the protagonist change by the end?",
    placeholder:
      "Describe the emotional journey, flaws, discoveries, and transformation.",
    group: "Story Pillars",
  },
  {
    name: "themeAndMoral",
    label: "Theme & Moral",
    hint: "What idea should stay with the audience?",
    placeholder:
      "Describe the central theme, moral dilemma, or question the film explores.",
    group: "Story Pillars",
  },
  {
    name: "targetMarketAndCulturalFocus",
    label: "Target Market & Cultural Focus",
    hint: "Which markets or cultural perspectives should shape the film?",
    placeholder:
      "e.g. Global audience, South Asian culture, culturally specific details.",
    group: "Film Direction",
  },
  {
    name: "releaseType",
    label: "Release Type",
    hint: "Where do you imagine the film being released?",
    placeholder: "e.g. Theatrical, streaming / OTT, festival circuit",
    group: "Film Direction",
  },
  {
    name: "primaryGenre",
    label: "Primary Genre",
    hint: "Choose the main genre or genre combination.",
    placeholder: "e.g. Science fiction thriller, drama, adventure",
    group: "Film Direction",
  },
  {
    name: "toneAndEmotionalExperience",
    label: "Tone & Emotional Experience",
    hint: "How should the audience feel while watching?",
    placeholder: "e.g. Tense, hopeful, mysterious, emotionally uplifting",
    group: "Film Direction",
  },
  {
    name: "targetAudience",
    label: "Target Audience",
    hint: "Who is the ideal audience for this film?",
    placeholder: "e.g. Young adults, family audiences, genre enthusiasts",
    group: "Film Direction",
  },
  {
    name: "featureFilmRuntime",
    label: "Feature Film Runtime",
    hint: "What is the intended runtime?",
    placeholder: "e.g. 100–120 minutes",
    group: "Film Direction",
  },
  {
    name: "storyScaleAndScope",
    label: "Story Scale & Scope",
    hint: "How large is the story in terms of locations, cast, and spectacle?",
    placeholder:
      "e.g. Intimate character drama, city-wide mystery, epic global adventure",
    group: "Film Direction",
  },
];

const initialForm = Object.fromEntries(
  REQUIRED_FIELDS.map((field) => [field, ""])
);

function readToken() {
  if (typeof window === "undefined") return "";

  for (const key of ["aiUser", "token", "accessToken", "user"]) {
    try {
      const raw = window.localStorage.getItem(key);

      if (!raw) continue;

      let parsed;

      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = raw;
      }

      const token =
        typeof parsed === "string"
          ? parsed
          : parsed?.token ||
            parsed?.accessToken ||
            parsed?.access_token ||
            parsed?.jwt;

      if (token) return token;

      if (
        key !== "user" &&
        typeof raw === "string" &&
        !raw.startsWith("{")
      ) {
        return raw;
      }
    } catch {
      // localStorage may be unavailable.
    }
  }

  return "";
}

function absoluteFileUrl(path) {
  if (!path || typeof path !== "string") return "";

  if (/^https?:\/\//i.test(path)) return path;

  const origin = API_ROOT.replace(/\/api\/v1\/?$/, "");

  return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
}

function formatMoney(value) {
  const number = Number(value);

  return Number.isFinite(number)
    ? `$${number.toFixed(number < 0.01 ? 5 : 4)}`
    : "—";
}

function unwrapResponse(value) {
  let current = value;

  if (
    current &&
    typeof current === "object" &&
    "data" in current &&
    current.data &&
    typeof current.data === "object"
  ) {
    current = current.data;
  }

  if (
    current &&
    typeof current === "object" &&
    "data" in current &&
    current.data &&
    typeof current.data === "object" &&
    !current.concept &&
    !current.script &&
    !current.productionPackage
  ) {
    current = current.data;
  }

  return current || {};
}

function StatusPill({ children, variant = "neutral" }) {
  return (
    <span className={`ff-status ff-status--${variant}`}>
      {children}
    </span>
  );
}

function FileLink({ href, label }) {
  const url = absoluteFileUrl(href);

  if (!url) {
    return (
      <span className="ff-file-unavailable">
        <span>↗</span>
        {label}
        <small>Not available yet</small>
      </span>
    );
  }

  return (
    <a
      className="ff-file-link"
      href={url}
      target="_blank"
      rel="noreferrer"
      download
    >
      <span className="ff-file-icon">↓</span>
      <span className="ff-file-label">{label}</span>
      <span className="ff-file-open">Open / download ↗</span>
    </a>
  );
}

function Score({ label, value }) {
  const score = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div className="ff-score">
      <div className="ff-score-label">
        <small>{label}</small>
        <strong>
          {score}
          <span>/100</span>
        </strong>
      </div>

      <div className="ff-score-track">
        <i style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

export default function NewFeatureFilmAI({
  websocketUrl = "",
  socket = null,
  apiRoot = API_ROOT,
}) {
  const location = useLocation();

  const [form, setForm] = useState(initialForm);
  const [sessionId, setSessionId] = useState(
    location.state?.sessionId || ""
  );
  const [sessionTitle, setSessionTitle] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [session, setSession] = useState(null);
  const [busyStep, setBusyStep] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [wsState, setWsState] = useState("connecting");
  const [history, setHistory] = useState([]);
  const [showAllFields, setShowAllFields] = useState(true);

  const socketRef = useRef(socket);
  const wsRef = useRef(null);
  const pendingStepRef = useRef("");
  const pendingTransactionRef = useRef("");
  const mountedRef = useRef(true);

  const root = String(apiRoot || API_ROOT).replace(/\/$/, "");

  const endpoints = useMemo(
    () => ({
      concept: `${root}/feature-film-ai/generate-concept`,
      script: `${root}/feature-film/generate-script`,
      production: `${root}/feature-film/generate-production-package`,
    }),
    [root]
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (wsRef.current && !socketRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const applySessionData = useCallback((payload, event = {}) => {
    const data = unwrapResponse(payload);

    if (!data || typeof data !== "object") return;

    setSession((previous) => {
      const next = {
        ...(previous || {}),
        ...data,
      };

      for (const key of [
        "inputs",
        "concept",
        "script",
        "scriptAudit",
        "productionPackage",
        "productionAudit",
      ]) {
        if (data[key] && typeof data[key] === "object") {
          next[key] = {
            ...(previous?.[key] || {}),
            ...data[key],
          };
        }
      }

      if (Array.isArray(data.attachments)) {
        next.attachments = data.attachments;
      }

      if (Array.isArray(data.history)) {
        next.history = data.history;
      }

      return next;
    });

    if (data._id || data.sessionId || event.sessionId) {
      setSessionId(data._id || data.sessionId || event.sessionId);
    }

    if (data.title || data.sessionName) {
      setSessionTitle(data.title || data.sessionName);
    }

    if (data.inputs) {
      setForm((previous) => ({
        ...previous,
        ...data.inputs,
      }));
    }

    if (Array.isArray(data.history)) {
      setHistory(data.history);
    }

    const stepValue = data.currentStep || event.step;

    if (stepValue) {
      const step = Math.max(
        1,
        Math.min(
          3,
          Number(String(stepValue).replace(/^0+/, "")) || 1
        )
      );

      setActiveStep((previous) => Math.max(previous, step));
    }
  }, []);

  const handleSocketMessage = useCallback(
    (raw) => {
      let event = raw;

      try {
        if (typeof raw === "string") {
          event = JSON.parse(raw);
        } else if (raw?.data && typeof raw.data === "string") {
          event = JSON.parse(raw.data);
        }
      } catch {
        return;
      }

      if (!event || typeof event !== "object") return;

      if (event.type && event.type !== "feature-film-ai") {
        return;
      }

      const action = event.action || "";

      const transactionId =
        event.transactionId || event.data?.transactionId || "";

      if (
        pendingTransactionRef.current &&
        transactionId &&
        transactionId !== pendingTransactionRef.current
      ) {
        return;
      }

      if (event.sessionId || event.data?._id) {
        setSessionId(event.sessionId || event.data?._id);
      }

      if (
        event.status === "failed" ||
        /failed|error/i.test(action)
      ) {
        setError(
          event.message || "Generation failed. Please try again."
        );

        setBusyStep("");
        pendingStepRef.current = "";
        pendingTransactionRef.current = "";

        return;
      }

      const completedActions = [
        "feature_film_concept_completed",
        "feature_film_script_completed",
        "feature_film_production_package_completed",
      ];

      const inferredCompleted =
        event.status === "completed" &&
        (event.data?.concept ||
          event.data?.script ||
          event.data?.productionPackage);

      if (
        completedActions.includes(action) ||
        inferredCompleted
      ) {
        applySessionData(event.data || event, event);

        setNotice(event.message || "Your film assets are ready.");
        setBusyStep("");
        pendingStepRef.current = "";
        pendingTransactionRef.current = "";

        const step =
          action === completedActions[0]
            ? 1
            : action === completedActions[1]
              ? 2
              : action === completedActions[2]
                ? 3
                : Number(event.step) || 1;

        setActiveStep(Math.max(1, Math.min(3, step)));
      } else if (
        event.status === "processing" ||
        event.status === "started" ||
        event.transactionId
      ) {
        setNotice(
          event.message ||
            "Request accepted. Waiting for the completed WebSocket event…"
        );
      }
    },
    [applySessionData]
  );

  useEffect(() => {
    if (socket) {
      socketRef.current = socket;
      setWsState("connected");

      const listener = (event) => {
        handleSocketMessage(event?.data ?? event);
      };

      if (socket.addEventListener) {
        socket.addEventListener("message", listener);
      } else if (socket.on) {
        socket.on("message", listener);
      }

      return () => {
        if (socket.removeEventListener) {
          socket.removeEventListener("message", listener);
        } else if (socket.off) {
          socket.off("message", listener);
        }
      };
    }

    if (!websocketUrl) {
      setWsState("not-configured");
      return undefined;
    }

    let ws;

    try {
      ws = new WebSocket(websocketUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsState("connected");

      ws.onmessage = (event) => {
        handleSocketMessage(event.data);
      };

      ws.onerror = () => setWsState("disconnected");

      ws.onclose = () => setWsState("disconnected");
    } catch {
      setWsState("disconnected");
    }

    return () => {
      if (ws && wsRef.current === ws) {
        ws.close();
      }
    };
  }, [socket, websocketUrl, handleSocketMessage]);

  useEffect(() => {
    const incomingId = location.state?.sessionId;

    if (incomingId) {
      setSessionId(incomingId);
    }
  }, [location.state?.sessionId]);

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const sendRequest = async (endpoint, body, step) => {
    if (busyStep) return;

    setError("");
    setNotice("");
    setBusyStep(step);

    pendingStepRef.current = step;
    pendingTransactionRef.current = "";

    try {
      const token = readToken();

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {}),
        },
        body: JSON.stringify(body),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(
          result.message ||
            result.error ||
            `Request failed (${response.status})`
        );
      }

      const payload = unwrapResponse(result);

      const transactionId =
        result.transactionId || payload.transactionId || "";

      if (transactionId) {
        pendingTransactionRef.current = transactionId;

        setSession((previous) => ({
          ...(previous || {}),
          transactionId,
        }));
      }

      if (
        payload.sessionId ||
        result.sessionId ||
        payload._id
      ) {
        setSessionId(
          payload.sessionId || result.sessionId || payload._id
        );
      }

      if (
        payload.data ||
        payload._id ||
        payload.currentStep ||
        payload.concept ||
        payload.script ||
        payload.productionPackage
      ) {
        const directData = payload.data || payload;

        const directComplete =
          directData.concept ||
          directData.script ||
          directData.productionPackage ||
          result.status === "completed";

        if (directComplete) {
          handleSocketMessage({
            ...result,
            type: result.type || "feature-film-ai",
            action:
              result.action ||
              (step === "01"
                ? "feature_film_concept_completed"
                : step === "02"
                  ? "feature_film_script_completed"
                  : "feature_film_production_package_completed"),
            status: "completed",
            step,
            sessionId: result.sessionId || directData._id,
            data: directData,
          });

          return;
        }

        applySessionData(directData, result);
      }

      setNotice(
        result.message ||
          payload.message ||
          "Request accepted. Waiting for the completed WebSocket event…"
      );

      if (!socketRef.current && !websocketUrl) {
        setNotice(
          (result.message || "Request accepted.") +
            " Connect the app's shared WebSocket using the socket or websocketUrl prop to receive the completed result."
        );
      }
    } catch (requestError) {
      setError(
        requestError.message ||
          "Something went wrong while sending your request."
      );

      setBusyStep("");
      pendingStepRef.current = "";
      pendingTransactionRef.current = "";
    }
  };

  const generateConcept = (event) => {
    event.preventDefault();

    const missing = REQUIRED_FIELDS.filter(
      (field) => !String(form[field] || "").trim()
    );

    if (missing.length) {
      const first = FIELD_CONFIG.find(
        (field) => field.name === missing[0]
      );

      setError(
        `Please complete all required fields. Missing: ${
          first?.label || missing[0]
        }.`
      );

      document
        .querySelector(`[name="${missing[0]}"]`)
        ?.focus();

      return;
    }

    sendRequest(
      endpoints.concept,
      {
        ...form,
        title: sessionTitle || "",
      },
      "01"
    );
  };

  const generateScript = () => {
    if (!sessionId) {
      setError(
        "Generate the film concept first. A session ID is required for screenplay generation."
      );

      return;
    }

    sendRequest(endpoints.script, { sessionId }, "02");
  };

  const generateProduction = () => {
    if (!sessionId) {
      setError(
        "A session ID is required. Please generate or reopen a film concept first."
      );

      return;
    }

    sendRequest(
      endpoints.production,
      { sessionId },
      "03"
    );
  };

  const concept = session?.concept || {};
  const script = session?.script || {};
  const scriptAudit = session?.scriptAudit || {};
  const production = session?.productionPackage || {};
  const productionAudit = session?.productionAudit || {};

  const attachments = Array.isArray(session?.attachments)
    ? session.attachments
    : [];

  const hasConcept = Boolean(
    concept.conceptText || concept.conceptFileUrl
  );

  const hasScript = Boolean(
    script.scriptContent ||
      script.scriptFileUrl ||
      script.scriptPdfUrl
  );

  const hasProduction = Boolean(
    production.packageFileUrl || production.packagePdfUrl
  );

  const stepDone = (step) =>
    step === 1
      ? hasConcept
      : step === 2
        ? hasScript
        : hasProduction;

  return (
    <main className="ff-page">
      <div
        className="ff-orb ff-orb-one"
        aria-hidden="true"
      />

      <div
        className="ff-orb ff-orb-two"
        aria-hidden="true"
      />

      <div className="ff-shell">
        <section className="ff-hero">
          <div className="ff-hero-copy">
            <div className="ff-eyebrow">
              <span className="ff-eyebrow-line" />
              YOUR STORY. THE BIG SCREEN.
            </div>

            <h1>
              From a spark of an idea
              <br />
              to a <span>feature film.</span>
            </h1>

            <p>
              Build your cinematic vision with an AI-powered
              workflow for concept development, screenplay writing,
              script audit, and production planning.
            </p>

            <div className="ff-hero-points">
              <span>✦ Story concept</span>
              <span>✦ Screenplay & audit</span>
              <span>✦ Production package</span>
            </div>
          </div>

          <div
            className="ff-hero-art"
            aria-label="Decorative cinematic film artwork"
          >
            <div className="ff-art-glow" />
            <div className="ff-film-ring ff-film-ring-a" />
            <div className="ff-film-ring ff-film-ring-b" />

            <div className="ff-film-card ff-film-card-back">
              <span>FRAME 02</span>
              <i />
            </div>

            <div className="ff-film-card ff-film-card-front">
              <span>FRAME 01</span>

              <div className="ff-mountain-scene">
                <i />
                <b />
                <em />
              </div>

              <small>THE ART OF STORYTELLING</small>
            </div>

            <div className="ff-art-star ff-star-one">✦</div>
            <div className="ff-art-star ff-star-two">✧</div>
          </div>
        </section>

        <section className="ff-workspace">
          <div className="ff-section-heading">
            <div>
              <span className="ff-kicker">
                YOUR FILM WORKSPACE
              </span>

              <h2>Production journey</h2>

              <p>
                One story session. Three creative milestones.
              </p>
            </div>

            <div className="ff-session-chip">
              <span
                className={`ff-session-dot ${
                  wsState === "connected" ? "is-connected" : ""
                }`}
              />

              {sessionId
                ? `SESSION ${String(sessionId)
                    .slice(-8)
                    .toUpperCase()}`
                : "NEW SESSION"}
            </div>
          </div>

          <nav
            className="ff-stepper"
            aria-label="Feature film steps"
          >
            {[
              {
                n: 1,
                step: "01",
                title: "Film Concept",
                subtitle: "Shape the story",
              },
              {
                n: 2,
                step: "02",
                title: "Screenplay & Audit",
                subtitle: "Write the script",
              },
              {
                n: 3,
                step: "03",
                title: "Production Package",
                subtitle: "Prepare to produce",
              },
            ].map((item, index) => (
              <Fragment key={item.step}>
                <button
                  type="button"
                  className={`ff-step ${
                    activeStep === item.n ? "is-active" : ""
                  } ${
                    stepDone(item.n) ? "is-complete" : ""
                  }`}
                  onClick={() => setActiveStep(item.n)}
                  aria-current={
                    activeStep === item.n ? "step" : undefined
                  }
                >
                  <span className="ff-step-number">
                    {stepDone(item.n) ? "✓" : item.step}
                  </span>

                  <span className="ff-step-copy">
                    <strong>{item.title}</strong>
                    <small>{item.subtitle}</small>
                  </span>
                </button>

                {index < 2 && (
                  <span
                    className={`ff-step-connector ${
                      stepDone(item.n + 1) ? "is-complete" : ""
                    }`}
                  />
                )}
              </Fragment>
            ))}
          </nav>

          {error && (
            <div
              className="ff-alert ff-alert-error"
              role="alert"
            >
              <span>!</span>
              <div>{error}</div>

              <button
                type="button"
                onClick={() => setError("")}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {notice && (
            <div
              className="ff-alert ff-alert-info"
              role="status"
            >
              <span>✦</span>

              <div>
                {notice}

                {busyStep && (
                  <small>
                    Keep this page open while the AI finishes.
                    The result will appear here when received.
                  </small>
                )}
              </div>

              <button
                type="button"
                onClick={() => setNotice("")}
                aria-label="Dismiss message"
              >
                ×
              </button>
            </div>
          )}

          {busyStep && (
            <div className="ff-generating">
              <div className="ff-spinner" />

              <div>
                <strong>
                  {busyStep === "01"
                    ? "Crafting your film concept"
                    : busyStep === "02"
                      ? "Writing your screenplay & audit"
                      : "Building your production package"}
                </strong>

                <p>
                  Your request has been sent. Waiting for the
                  completed WebSocket event…
                </p>
              </div>

              <span className="ff-generating-pulse">
                AI WORKING
              </span>
            </div>
          )}

          {activeStep === 1 && (
            <section className="ff-panel">
              <div className="ff-panel-heading">
                <div>
                  <span className="ff-panel-index">
                    01 / STORY DEVELOPMENT
                  </span>

                  <h3>Let’s build your film concept</h3>

                  <p>
                    Start with the core pillars of your story.
                    The more specific your vision, the stronger
                    the concept.
                  </p>
                </div>

                <div className="ff-heading-icon">✧</div>
              </div>

              <form onSubmit={generateConcept}>
                <div className="ff-form-topline">
                  <span>
                    <b>12</b> story pillars
                  </span>

                  <span>
                    <i className="ff-required-dot" />
                    All fields required
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setShowAllFields((value) => !value)
                    }
                  >
                    {showAllFields
                      ? "Collapse form ↑"
                      : "Expand form ↓"}
                  </button>
                </div>

                {showAllFields && (
                  <div className="ff-field-grid">
                    {FIELD_CONFIG.map((field, index) => (
                      <label
                        className={`ff-field ${
                          index < 5 ? "ff-field-wide" : ""
                        }`}
                        key={field.name}
                      >
                        <span className="ff-field-label">
                          {String(index + 1).padStart(2, "0")}{" "}
                          <b>{field.label}</b>
                          <em>*</em>
                        </span>

                        <span className="ff-field-hint">
                          {field.hint}
                        </span>

                        <textarea
                          name={field.name}
                          value={form[field.name]}
                          onChange={updateField}
                          placeholder={field.placeholder}
                          rows={index < 5 ? 3 : 2}
                          required
                        />
                      </label>
                    ))}
                  </div>
                )}

                {!showAllFields && (
                  <div className="ff-collapsed-note">
                    Your story fields are hidden. Expand the form
                    to review or edit all 12 required fields.
                  </div>
                )}

                <div className="ff-form-footer">
                  <div>
                    <span className="ff-lock">▣</span>

                    <span>
                      Your story inputs are sent with the concept
                      request and saved to the film session by
                      the API.
                    </span>
                  </div>

                  <button
                    className="ff-primary-button"
                    type="submit"
                    disabled={Boolean(busyStep)}
                  >
                    <span>✦</span>

                    {busyStep === "01"
                      ? "Generating concept…"
                      : hasConcept
                        ? "Regenerate film concept"
                        : "Generate film concept"}

                    <b>→</b>
                  </button>
                </div>
              </form>

              {hasConcept && (
                <div className="ff-result-card">
                  <div className="ff-result-heading">
                    <div>
                      <span className="ff-panel-index">
                        CONCEPT GENERATED
                      </span>

                      <h3>
                        {concept.title ||
                          session?.title ||
                          "Your Film Concept"}
                      </h3>
                    </div>

                    <StatusPill variant="success">
                      Ready
                    </StatusPill>
                  </div>

                  <div className="ff-prose">
                    {concept.conceptText ||
                      "Concept text is available in the downloadable document."}
                  </div>

                  <div className="ff-result-meta">
                    <span>
                      Generation cost{" "}
                      <b>{formatMoney(concept.cost)}</b>
                    </span>

                    <span>
                      Updated{" "}
                      {concept.updatedAt
                        ? new Date(
                            concept.updatedAt
                          ).toLocaleString()
                        : "just now"}
                    </span>
                  </div>

                  <FileLink
                    href={concept.conceptFileUrl}
                    label={
                      concept.conceptFileName ||
                      "Download concept TXT"
                    }
                  />

                  <div className="ff-next-row">
                    <span>
                      Concept ready? Continue to screenplay and
                      audit.
                    </span>

                    <button
                      className="ff-primary-button"
                      type="button"
                      onClick={() => setActiveStep(2)}
                    >
                      Continue to Step 02 →
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeStep === 2 && (
            <section className="ff-panel">
              <div className="ff-panel-heading">
                <div>
                  <span className="ff-panel-index">
                    02 / SCREENPLAY DEVELOPMENT
                  </span>

                  <h3>
                    Turn the concept into a screenplay
                  </h3>

                  <p>
                    Generate a feature screenplay and an AI audit
                    with readiness recommendations.
                  </p>
                </div>

                <div className="ff-heading-icon">▤</div>
              </div>

              <div className="ff-context-card">
                <div className="ff-context-icon">✦</div>

                <div>
                  <small>ACTIVE FILM SESSION</small>

                  <strong>
                    {sessionTitle ||
                      session?.title ||
                      concept.title ||
                      "Your feature film"}
                  </strong>

                  <p>
                    {sessionId
                      ? `Session ID: ${sessionId}`
                      : "Generate a concept first to create a session."}
                  </p>
                </div>

                <StatusPill
                  variant={sessionId ? "success" : "neutral"}
                >
                  {sessionId ? "Session linked" : "No session"}
                </StatusPill>
              </div>

              <div className="ff-action-card">
                <div className="ff-action-icon">✎</div>

                <div className="ff-action-copy">
                  <h4>Screenplay & script audit</h4>

                  <p>
                    Build the screenplay, downloadable PDF, text
                    file, and script audit with theatrical and
                    OTT scores.
                  </p>

                  <div className="ff-mini-tags">
                    <span>Screenplay TXT</span>
                    <span>Screenplay PDF</span>
                    <span>Audit report</span>
                  </div>
                </div>

                <button
                  className="ff-primary-button"
                  type="button"
                  onClick={generateScript}
                  disabled={Boolean(busyStep) || !sessionId}
                >
                  {busyStep === "02"
                    ? "Generating…"
                    : hasScript
                      ? "Regenerate screenplay →"
                      : "Generate screenplay →"}
                </button>
              </div>

              {hasScript && (
                <div className="ff-result-card">
                  <div className="ff-result-heading">
                    <div>
                      <span className="ff-panel-index">
                        SCREENPLAY
                      </span>

                      <h3>
                        {script.title ||
                          session?.title ||
                          "Screenplay"}
                      </h3>
                    </div>

                    <StatusPill variant="success">
                      Generated
                    </StatusPill>
                  </div>

                  <div className="ff-prose ff-script-preview">
                    {script.scriptContent ||
                      "Screenplay text is available in the downloadable files."}
                  </div>

                  <div className="ff-file-grid">
                    <FileLink
                      href={script.scriptFileUrl}
                      label="Screenplay TXT"
                    />

                    <FileLink
                      href={script.scriptPdfUrl}
                      label="Screenplay PDF"
                    />

                    <FileLink
                      href={scriptAudit.auditFileUrl}
                      label="Audit report TXT"
                    />

                    <FileLink
                      href={scriptAudit.auditPdfUrl}
                      label="Audit report PDF"
                    />
                  </div>

                  <div className="ff-audit-panel">
                    <div className="ff-audit-top">
                      <div>
                        <span className="ff-panel-index">
                          SCRIPT AUDIT
                        </span>

                        <h4>Readiness assessment</h4>
                      </div>

                      <StatusPill
                        variant={
                          /APPROVED|GREENLIT|PROCEED/i.test(
                            scriptAudit.status ||
                              scriptAudit.recommendation ||
                              ""
                          )
                            ? "success"
                            : "neutral"
                        }
                      >
                        {scriptAudit.status ||
                          "Audit status pending"}
                      </StatusPill>
                    </div>

                    <p>
                      {scriptAudit.recommendation ||
                        "The audit recommendation will appear here when supplied by the API."}
                    </p>

                    <div className="ff-score-grid">
                      <Score
                        label="THEATRICAL SCORE"
                        value={scriptAudit.theatricalScore}
                      />

                      <Score
                        label="OTT SCORE"
                        value={scriptAudit.ottScore}
                      />
                    </div>
                  </div>

                  <div className="ff-result-meta">
                    <span>
                      Screenplay cost{" "}
                      <b>{formatMoney(script.cost)}</b>
                    </span>

                    <span>
                      Audit cost{" "}
                      <b>{formatMoney(scriptAudit.cost)}</b>
                    </span>
                  </div>

                  <div className="ff-next-row">
                    <span>
                      Screenplay ready? Build your production
                      package.
                    </span>

                    <button
                      className="ff-primary-button"
                      type="button"
                      onClick={() => setActiveStep(3)}
                    >
                      Continue to Step 03 →
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeStep === 3 && (
            <section className="ff-panel">
              <div className="ff-panel-heading">
                <div>
                  <span className="ff-panel-index">
                    03 / PRODUCTION PREPARATION
                  </span>

                  <h3>Prepare your production package</h3>

                  <p>
                    Generate the master production documents and
                    final audit for your film.
                  </p>
                </div>

                <div className="ff-heading-icon">▣</div>
              </div>

              <div className="ff-production-banner">
                <div className="ff-production-art">
                  <span>FF</span>
                  <i />
                  <b />
                </div>

                <div>
                  <span className="ff-panel-index">
                    FINAL MILESTONE
                  </span>

                  <h4>
                    {sessionTitle ||
                      session?.title ||
                      concept.title ||
                      "Your Feature Film"}
                  </h4>

                  <p>
                    Compile your film’s production package, PDF,
                    audit report, and project attachments into
                    one workspace.
                  </p>

                  <div className="ff-mini-tags">
                    <span>Production TXT</span>
                    <span>Production PDF</span>
                    <span>Final audit</span>
                  </div>
                </div>
              </div>

              <div className="ff-action-card">
                <div className="ff-action-icon">▤</div>

                <div className="ff-action-copy">
                  <h4>16 master production files & audit</h4>

                  <p>
                    Generate production-ready documentation with
                    a greenlight recommendation and pre-production
                    readiness status.
                  </p>
                </div>

                <button
                  className="ff-primary-button"
                  type="button"
                  onClick={generateProduction}
                  disabled={Boolean(busyStep) || !sessionId}
                >
                  {busyStep === "03"
                    ? "Building…"
                    : hasProduction
                      ? "Regenerate production package →"
                      : "Generate production package →"}
                </button>
              </div>

              {hasProduction ? (
                <div className="ff-result-card">
                  <div className="ff-result-heading">
                    <div>
                      <span className="ff-panel-index">
                        PRODUCTION PACKAGE
                      </span>

                      <h3>
                        {session?.title || "Your Production Files"}
                      </h3>
                    </div>

                    <StatusPill
                      variant={
                        /GREENLIT|READY/i.test(
                          `${productionAudit.status || ""} ${
                            productionAudit.recommendation || ""
                          }`
                        )
                          ? "success"
                          : "neutral"
                      }
                    >
                      {productionAudit.status || "Generated"}
                    </StatusPill>
                  </div>

                  <div className="ff-file-grid">
                    <FileLink
                      href={production.packageFileUrl}
                      label="Production package TXT"
                    />

                    <FileLink
                      href={production.packagePdfUrl}
                      label="Production package PDF"
                    />

                    <FileLink
                      href={productionAudit.auditFileUrl}
                      label="Production audit TXT"
                    />

                    <FileLink
                      href={productionAudit.auditPdfUrl}
                      label="Production audit PDF"
                    />
                  </div>

                  <div className="ff-audit-panel">
                    <div className="ff-audit-top">
                      <div>
                        <span className="ff-panel-index">
                          PRODUCTION AUDIT
                        </span>

                        <h4>Greenlight assessment</h4>
                      </div>

                      <StatusPill
                        variant={
                          /GREENLIT|READY/i.test(
                            `${productionAudit.status || ""} ${
                              productionAudit.recommendation || ""
                            }`
                          )
                            ? "success"
                            : "neutral"
                        }
                      >
                        {productionAudit.status || "Pending"}
                      </StatusPill>
                    </div>

                    <p>
                      {productionAudit.recommendation ||
                        "Your production recommendation will appear here."}
                    </p>
                  </div>

                  <div className="ff-result-meta">
                    <span>
                      Package cost{" "}
                      <b>{formatMoney(production.cost)}</b>
                    </span>

                    <span>
                      Audit cost{" "}
                      <b>{formatMoney(productionAudit.cost)}</b>
                    </span>

                    <span>
                      Total project cost{" "}
                      <b>{formatMoney(session?.totalCost)}</b>
                    </span>
                  </div>
                </div>
              ) : (
                <div className="ff-empty-state">
                  <div>✧</div>

                  <h4>
                    Your production package will live here
                  </h4>

                  <p>
                    Generate the package to receive production
                    documents and the final audit.
                  </p>
                </div>
              )}
            </section>
          )}

          <section className="ff-overview">
            <div className="ff-overview-title">
              <div>
                <span className="ff-kicker">
                  PROJECT OVERVIEW
                </span>

                <h3>Everything in one place</h3>
              </div>

              <span className="ff-overview-count">
                {attachments.length} FILES
              </span>
            </div>

            <div className="ff-overview-grid">
              <div
                className={`ff-overview-item ${
                  hasConcept ? "is-ready" : ""
                }`}
              >
                <span className="ff-overview-icon">✧</span>

                <div>
                  <strong>Film concept</strong>

                  <small>
                    {hasConcept
                      ? "Concept document available"
                      : "Waiting for Step 01"}
                  </small>
                </div>

                <StatusPill
                  variant={hasConcept ? "success" : "neutral"}
                >
                  {hasConcept ? "Ready" : "Pending"}
                </StatusPill>
              </div>

              <div
                className={`ff-overview-item ${
                  hasScript ? "is-ready" : ""
                }`}
              >
                <span className="ff-overview-icon">▤</span>

                <div>
                  <strong>Screenplay & audit</strong>

                  <small>
                    {hasScript
                      ? "Script and audit available"
                      : "Waiting for Step 02"}
                  </small>
                </div>

                <StatusPill
                  variant={hasScript ? "success" : "neutral"}
                >
                  {hasScript ? "Ready" : "Pending"}
                </StatusPill>
              </div>

              <div
                className={`ff-overview-item ${
                  hasProduction ? "is-ready" : ""
                }`}
              >
                <span className="ff-overview-icon">▣</span>

                <div>
                  <strong>Production package</strong>

                  <small>
                    {hasProduction
                      ? "Production documents available"
                      : "Waiting for Step 03"}
                  </small>
                </div>

                <StatusPill
                  variant={hasProduction ? "success" : "neutral"}
                >
                  {hasProduction ? "Ready" : "Pending"}
                </StatusPill>
              </div>
            </div>

            {attachments.length > 0 && (
              <div className="ff-attachments">
                <h4>Session attachments</h4>

                {attachments.map((file, index) => (
                  <FileLink
                    key={`${file}-${index}`}
                    href={file}
                    label={
                      String(file).split("/").pop() ||
                      `Attachment ${index + 1}`
                    }
                  />
                ))}
              </div>
            )}

            {history.length > 0 && (
              <details className="ff-history">
                <summary>
                  Session history ({history.length})
                </summary>

                {history.map((item, index) => (
                  <div
                    className="ff-history-row"
                    key={`${item.step || index}-${
                      item.createdAt || index
                    }`}
                  >
                    <span>STEP {item.step || "—"}</span>

                    <strong>
                      {item.title || "Film update"}
                    </strong>

                    <small>
                      {item.createdAt
                        ? new Date(
                            item.createdAt
                          ).toLocaleString()
                        : ""}
                    </small>
                  </div>
                ))}
              </details>
            )}
          </section>
        </section>
      </div>
    </main>
  );
}
